import * as React from 'react';
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LOANS } from "../../locales/namespaces";
import DataGridWrapper from "../../../../components/dataGridWrapper/dataGridWrapper";
import { muiGridLocales } from "../../../../helpers/muiGridLocales";
import MainCard from "../../../../theme/mantis/components/mainCard";
import moment from "moment";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { Box, Grid, Typography, styled, useMediaQuery } from "@mui/material";
import NotificationGrid from "../../../../assets/images/icons/notificationGrid";
import AlertTriangleIcon from "../../../../assets/images/icons/alertTriangleIcon";
import { UnsortedIcon } from "../../../../assets/images/icons/sortIcon";
import { Capacitor } from "@capacitor/core";
import { useTheme } from "@mui/material/styles";
import { color } from 'framer-motion';

const StyledDataGrid = styled(DataGridPremium)(({ theme }) => ({
	'& .MuiDataGrid-cell--withRenderer': {
		border: 'none !important'
	},
	'& .date-created-at .MuiDataGrid-cellContent':{
		fontSize: '12px',
		color: '#8891AC',
		fontWeight: '500',
		lineHeight: '18px',
	},
	'& .Type-1': {
		backgroundColor: '#A7605B',
	},
	'& .Type-3': {
		backgroundColor: '#485476',
	},
	' .MuiDataGrid-row:nth-child(2n+1)': {
		background: '#F5F7FC',
	}
}));

const NotesTable = ({ loanNotes }) => {
	const { t } = useTranslation(LOANS)
	const theme = useTheme();
	const matchBetweenXSSM = useMediaQuery(theme.breakpoints.between('xs', 'sm'));


	const columns = useMemo(() => {
		if (matchBetweenXSSM) {
			return [
				{
					field: 'icon',
					headerName: '',
					maxWidth: 40, flex: 1,
					renderCell: ({ row }) => (
						row.Type === "3" ? 
							<NotificationGrid color={'#519C66'} /> 
							: 
							<AlertTriangleIcon color={'#F4A40B'} />
					),
					cellClassName: (params) => `MuiDataGrid-cell--icon Type-${params.row.Type}`,
				},
				{
					field: 'details',
					headerName: t('Details'),
					flex: 3,
					sortable: false,
					renderCell: ({ row }) => {
						const stripHtmlTags = (html) => {
							const doc = new DOMParser().parseFromString(html, 'text/html');
							return doc.body.textContent || "";
						};
						const strippedBody = stripHtmlTags(row.Body);
						return (
							<Box>
								<Typography fontWeight={700} fontSize="14px" color="#485776">
									{row.Type === "3"
										? t("Change loan application status")
										: t("Update for pending")}
								</Typography>
								<Typography fontSize="12px" color="text.secondary">
									{strippedBody}
								</Typography>
								<Typography fontSize="11px" color="gray">
									{moment(row.CreatedAt).format("DD/MM/YY h a")}
								</Typography>
							</Box>
						);
					}
				}
			];
		}
	
		// Default: full view
		return [
			{
				field: 'icon',
				headerName: '',
				maxWidth: 40, flex: 1,
				renderCell: ({ row }) => {
					return (
						(row.Type === "3") ?
	
							<NotificationGrid
								color={'#519C66'}
							/>
	
							:
	
							<AlertTriangleIcon
								color={'#F4A40B'}
							/>
	
					)
				},
				cellClassName: (params) => `MuiDataGrid-cell--icon Type-${params.row.Type}`
			},
			{
				field: 'text',
				headerName: t('Updates'),
				maxWidth: 400, flex: 1,
				renderCell: ({ row }) => {
					return (
						(row.Type === "3") ?
							<Typography
								color={'#485476'}
								sx={{
									fontSize: Capacitor.isNativePlatform() ? '12px' : '12px',
									fontStyle: 'normal',
									fontWeight: Capacitor.isNativePlatform() ? 500 : 500,
									lineHeight: '18px'
								}}
							>
								{t("Change loan application status")}
							</Typography>
							:
							<Typography
								color={'#485476'}
								sx={{
									fontSize: Capacitor.isNativePlatform() ? '12px' : '12px',
									fontStyle: 'normal',
									fontWeight: Capacitor.isNativePlatform() ? 500 : 500,
									lineHeight: '18px'
								}}
							>
								{t("Update for pending")}
							</Typography>
					)
				}
			},
			{
				field: 'Body',
				headerName: '',
				minWidth: 330, flex: 1,
				renderCell: ({ row }) => {
					const stripHtmlTags = (html) => {
						const doc = new DOMParser().parseFromString(html, 'text/html');
						return doc.body.textContent || "";
					};
					const strippedBody = stripHtmlTags(row.Body);
					return (
						<Typography component="span" color={'#8891AC'} fontSize={'12px'} sx={{padding: '10px 0'}} >
							{strippedBody}
						</Typography>
					)
				}
			},
			{
				field: 'CreatedAt',
				headerName: t('Date'),
				minWidth: 100, maxWidth: matchBetweenXSSM ? 100 : 140, flex: 1,
				valueFormatter: params =>
					moment(params?.value).format("DD/MM/YY h a"),
				cellClassName: 'date-created-at',
			},
	
		];
	}, [loanNotes, t, matchBetweenXSSM]);
	

	return (
		<MainCard
			className="container-table-notes"
			contentSX={{ p: 0 }}
			sx={{
				border: '0.6px solid #DAE0F0',
				"& .MuiCardContent-root":{
					paddingBottom: '0 !important'
				},
			}}
		>
			<DataGridWrapper
				sx={{
					overflow: 'auto'
				}}
			>
				<StyledDataGrid
					hideFooter
					rows={loanNotes}
					columns={columns}
					getRowClassName={(params) => {
						return (`super-app-theme--${params.row.Type}`)
					}}
					sx={{

						"& .MuiDataGrid-columnHeaderTitle": {
							fontSize: '12px',
							color: '#485476',
							fontWeight: '500',
							lineHeight: '14px',
						},
						bgcolor: 'background.paper',
						".MuiDataGrid-aggregationColumnHeaderLabel": {
							display: "none"
						},
						".MuiDataGrid-cellContent": {
							fontSize: '0.875rem'
						}
					}}
					density={'compact'}
					localeText={{ ...muiGridLocales(t), noRowsLabel: t('There are no updates.') }}
					disableAggregation={true}
					disableRowGrouping={true}
					disableColumnMenu={true}
					slots={{
						columnUnsortedIcon: UnsortedIcon,
					}}
				/>
			</DataGridWrapper>
		</MainCard>
	)
}

export default NotesTable